import PropTypes from "prop-types";
import React from "react";
import { Value } from "react-powerplug";
import AtomicBlockIdentifiers from "../AtomicBlockIdentifiers";
import {
  AirtableIcon,
  DividerIcon,
  GoogleFormsIcon,
  ImageIcon,
  SimplecastIcon,
  SocialMediaIcon,
  SpreadsheetIcon,
  TypeformIcon,
  VideoIcon,
  NewsletterIcon,
  CustomButtonIcon,
} from "../icons";
import {
  AirtableInput,
  GoogleSpreadsheetsInput,
  SidebarButton,
  SidebarTray,
  SimplecastInput,
  TypeformInput,
  YoutubeInput,
} from "../sidebar";
import GoogleFormsInput from "../sidebar/GoogleFormsInput";
import SocialMediaInput from "../sidebar/SocialMediaInput";
import testId from "../testId";
import { insertAtomicBlock } from "../utils";
import NewsletterInput from "../sidebar/NewsletterInput";
import CustomButtonInput from "../sidebar/CustomButtonInput";
import ImageInput from "../sidebar/ImageInput";

export default function StandardSidebarTray({ editorState, onChange }) {
  return (
    <Value initial={null}>
      {({ value, set }) => {
        const toggle = (type) => () => set(value === type ? null : type);
        const cancel = () => set(null);

        if (value === AtomicBlockIdentifiers.IMAGE) {
          return (
            <ImageInput
              onChange={(url, alt) => {
                onChange(
                  insertAtomicBlock(editorState, AtomicBlockIdentifiers.IMAGE, {
                    imageUrl: url,
                    imageAlt: alt,
                  }),
                );
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.YOUTUBE_VIDEO) {
          return (
            <SidebarTray>
              <YoutubeInput
                onCancel={cancel}
                onInsert={({ videoId, videoUrl }) => {
                  onChange(
                    insertAtomicBlock(editorState, AtomicBlockIdentifiers.YOUTUBE_VIDEO, {
                      videoId,
                      videoUrl,
                    }),
                  );
                }}
              />
            </SidebarTray>
          );
        } else if (value === AtomicBlockIdentifiers.GOOGLE_SPREADSHEET) {
          return (
            <GoogleSpreadsheetsInput
              onCancel={cancel}
              onInsert={(src) => {
                onChange(
                  insertAtomicBlock(editorState, AtomicBlockIdentifiers.GOOGLE_SPREADSHEET, {
                    src,
                    displayHeaders: true,
                  }),
                );
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.GOOGLE_FORMS) {
          return (
            <GoogleFormsInput
              onCancel={cancel}
              onInsert={(blockData) => {
                onChange(insertAtomicBlock(editorState, AtomicBlockIdentifiers.GOOGLE_FORMS, blockData));
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.TYPEFORM) {
          return (
            <TypeformInput
              onCancel={cancel}
              onInsert={(src) => {
                onChange(
                  insertAtomicBlock(editorState, AtomicBlockIdentifiers.TYPEFORM, {
                    src,
                  }),
                );
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.SIMPLECAST) {
          return (
            <SimplecastInput
              onCancel={cancel}
              onInsert={({ src, height }) => {
                onChange(
                  insertAtomicBlock(editorState, AtomicBlockIdentifiers.SIMPLECAST, {
                    src,
                    height,
                  }),
                );
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.AIRTABLE) {
          return (
            <AirtableInput
              onCancel={cancel}
              onInsert={(blockData) => {
                onChange(insertAtomicBlock(editorState, AtomicBlockIdentifiers.AIRTABLE, blockData));
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.SOCIAL_MEDIAS) {
          return (
            <SocialMediaInput
              onCancel={cancel}
              onInsert={(blockData) => {
                onChange(insertAtomicBlock(editorState, AtomicBlockIdentifiers.SOCIAL_MEDIAS, blockData));
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.NEWSLETTER) {
          return (
            <NewsletterInput
              onCancel={cancel}
              onInsert={(blockData) => {
                onChange(insertAtomicBlock(editorState, AtomicBlockIdentifiers.NEWSLETTER, blockData));
              }}
            />
          );
        } else if (value === AtomicBlockIdentifiers.CUSTOM_BUTTON) {
          return (
            <CustomButtonInput
              onCancel={cancel}
              onInsert={(blockData) => {
                onChange(insertAtomicBlock(editorState, AtomicBlockIdentifiers.CUSTOM_BUTTON, blockData));
              }}
            />
          );
        }

        return (
          <SidebarTray>
            <SidebarButton
              {...testId("StandardSidebarTray/buttons/image")}
              onClick={toggle(AtomicBlockIdentifiers.IMAGE)}
            >
              <ImageIcon />
            </SidebarButton>

            <SidebarButton onClick={toggle(AtomicBlockIdentifiers.YOUTUBE_VIDEO)}>
              <VideoIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/divider")}
              onClick={() => {
                onChange(insertAtomicBlock(editorState, AtomicBlockIdentifiers.DIVIDER));
              }}
            >
              <DividerIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/airtable")}
              onClick={toggle(AtomicBlockIdentifiers.AIRTABLE)}
            >
              <AirtableIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/googleSpreadsheet")}
              onClick={toggle(AtomicBlockIdentifiers.GOOGLE_SPREADSHEET)}
            >
              <SpreadsheetIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/googleForms")}
              onClick={toggle(AtomicBlockIdentifiers.GOOGLE_FORMS)}
            >
              <GoogleFormsIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/typeform")}
              onClick={toggle(AtomicBlockIdentifiers.TYPEFORM)}
            >
              <TypeformIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/simplecast")}
              onClick={toggle(AtomicBlockIdentifiers.SIMPLECAST)}
            >
              <SimplecastIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/socialMedias")}
              onClick={toggle(AtomicBlockIdentifiers.SOCIAL_MEDIAS)}
            >
              <SocialMediaIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/newsletter")}
              onClick={toggle(AtomicBlockIdentifiers.NEWSLETTER)}
            >
              <NewsletterIcon />
            </SidebarButton>

            <SidebarButton
              {...testId("StandardSidebarTray/buttons/customButton")}
              onClick={toggle(AtomicBlockIdentifiers.CUSTOM_BUTTON)}
            >
              <CustomButtonIcon />
            </SidebarButton>
          </SidebarTray>
        );
      }}
    </Value>
  );
}

StandardSidebarTray.propTypes = {
  onChange: PropTypes.func.isRequired,
  editorState: PropTypes.object.isRequired,
};
